import { fontById, fontStack, type EngraveFont } from './fonts';
import { LOGO_RATIO } from './logo';
import { CAP_STEPS_MM, LOGO_CAP_MM, type EngraveState, type FreePlace, type SlotKey, type SlotStyle } from './state';
import { mirrorZone, type EngraveZone } from './zones';

/**
 * A110Z — where a text sits on a temple. Pure maths, shared by the preview, the order sheet and the tests.
 *
 * The zone (engrave-zones.json) is the straight, flat part of the temple near the hinge, in fractions of the
 * detoured image whose hinge is on the right. The temple is shown mirrored when its hinge must be on the left
 * (left temple seen from inside, right temple seen from outside); the zone is mirrored with it, so every
 * computation here happens in the displayed image.
 *
 * Texts are drawn at the font's natural proportions — never stretched or condensed — at a real capital height
 * (A110Z2: mm notches, `CAP_STEPS_MM`, turned into pixels by the photo's mm per pixel).
 *
 * A110Z3: two placements.
 *   • automatic (style.place = null): along the zone's middle line, from its hinge side, tilted like the zone;
 *     a text longer than the zone is reduced uniformly, a capital height above the temple's thickness is capped;
 *   • free (style.place): the centre at `x` mm from the hinge along the temple and `y` mm across it, tilted by
 *     `rot` degrees from the temple's axis, at exactly the chosen height — the preview then says when the text
 *     leaves the temple or touches the hinge (`scene.ts`).
 * Both are returned as a centre, an angle and a size, drawn centred (text-anchor middle).
 */
export interface ZoneBox {
  /** Centre of the zone in displayed-image pixels. */
  cx: number;
  cy: number;
  /** Length along the temple and useful thickness (px). */
  len: number;
  thick: number;
  /** Rotation of the zone (degrees, image coordinates: positive turns clockwise). */
  angleDeg: number;
  /** The hinge is at the left end of the zone (mirrored temple). */
  hingeLeft: boolean;
  /** Millimetres per displayed pixel. */
  mmPerPx: number;
  /** Distance from the hinge end to the zone's hinge-side end (mm). */
  zoneStartMm: number;
}

export interface TextLayout {
  fontSize: number;
  /** Centre of the text (px) and its angle (degrees, image coordinates). */
  cx: number;
  cy: number;
  angleDeg: number;
  /** Capital height actually drawn (mm). */
  capMm: number;
  /** Automatic placement only: limited by the temple's thickness / reduced to the zone's length. */
  capped: boolean;
  reduced: boolean;
  /** Width of the text as drawn (px) and its capital height (px). */
  width: number;
  capPx: number;
  /** Placed by hand. */
  free: boolean;
}

export interface LogoLayout {
  /** Top-left corner of the wordmark box before rotation (px), its size, and the rotation about the zone centre. */
  x: number;
  y: number;
  width: number;
  height: number;
  angleDeg: number;
  cx: number;
  cy: number;
}

/** Capitals fill at most this share of the flat part's thickness (automatic placement). */
export const MAX_CAP_FRACTION = 0.62;
/** Capital height / font size when the font cannot be measured. */
export const CAP_RATIO_FALLBACK = 0.7;
/** Breathing space at both ends of the zone (fraction of its length). */
export const ZONE_PAD = 0.05;
/** Depth of descenders below the baseline (em), for the « inside the temple » check. */
export const DESCENT_EM = 0.2;

/** Measures a text at a font size: width in the same pixels (letter spacing included). */
export type TextMeasure = (text: string, fontSizePx: number) => number;
/** A font's measuring tools: text width, and capital height per unit of font size. */
export interface FontMetrics { measure: TextMeasure; capRatio: number }

/** Zone of the displayed image (mirrored or not), in pixels. */
export function zoneBox(zone: EngraveZone, width: number, height: number, mirrored: boolean, mmPerPx: number, zoneStartMm = 9): ZoneBox {
  const z = mirrored ? mirrorZone(zone) : zone;
  return { cx: (z.x + z.w / 2) * width, cy: z.y * height, len: z.w * width, thick: z.h * height, angleDeg: z.angle, hingeLeft: mirrored, mmPerPx, zoneStartMm };
}

/** A point at (u, v) in the zone's frame (u along the temple from its centre, v down) → image pixels. */
export function zonePoint(box: ZoneBox, u: number, v: number): { x: number; y: number } {
  const rad = (box.angleDeg * Math.PI) / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  return { x: box.cx + u * c - v * s, y: box.cy + u * s + v * c };
}

/** Image pixels → the zone's frame (inverse of `zonePoint`). */
export function zoneCoords(box: ZoneBox, x: number, y: number): { u: number; v: number } {
  const rad = (box.angleDeg * Math.PI) / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  const dx = x - box.cx, dy = y - box.cy;
  return { u: dx * c + dy * s, v: -dx * s + dy * c };
}

/** Direction of « away from the hinge » along u (+1 when the hinge is on the left). */
export function tipward(box: ZoneBox): 1 | -1 {
  return box.hingeLeft ? 1 : -1;
}

/** u of the zone's hinge-side end. */
function hingeEndU(box: ZoneBox): number {
  return box.hingeLeft ? -box.len / 2 : box.len / 2;
}

/** Free placement (mm) → centre in image pixels. */
export function placePoint(box: ZoneBox, place: FreePlace): { x: number; y: number } {
  const u = hingeEndU(box) + tipward(box) * ((place.x - box.zoneStartMm) / box.mmPerPx);
  return zonePoint(box, u, place.y / box.mmPerPx);
}

/** Centre in image pixels → free placement (mm), with a tilt. */
export function pointPlace(box: ZoneBox, x: number, y: number, rot: number): FreePlace {
  const { u, v } = zoneCoords(box, x, y);
  return { x: box.zoneStartMm + tipward(box) * (u - hingeEndU(box)) * box.mmPerPx, y: v * box.mmPerPx, rot };
}

/** Tallest capitals the automatic placement takes on this temple (mm). */
export function maxCapMm(box: ZoneBox): number {
  return box.thick * box.mmPerPx * MAX_CAP_FRACTION;
}

/** Displayed text: the font's capitals when it is an all-caps face. */
export function displayText(text: string, font: EngraveFont): string {
  return font.uppercase ? text.toUpperCase() : text;
}

/**
 * Room an automatically placed text has beyond its zone (A201): true when its box stays on the temple (and off the
 * hinge) — given by the scene from the temple's outline. Without it, a long text is reduced to fit the zone.
 */
export type TextRoom = (layout: TextLayout) => boolean;

export function layoutText(box: ZoneBox, font: EngraveFont, text: string, style: SlotStyle, metrics: FontMetrics, room?: TextRoom): TextLayout | null {
  const shown = displayText(text, font);
  if (!shown) return null;
  const capRatio = metrics.capRatio > 0.2 ? metrics.capRatio : CAP_RATIO_FALLBACK;
  if (style.place) {
    // Free: exactly the chosen height, where the customer put it.
    const fontSize = style.cap / box.mmPerPx / capRatio;
    const width = metrics.measure(shown, fontSize);
    const p = placePoint(box, style.place);
    return { fontSize, cx: p.x, cy: p.y, angleDeg: box.angleDeg + style.place.rot, capMm: style.cap, capped: false, reduced: false, width, capPx: fontSize * capRatio, free: true };
  }
  const avail = Math.max(1, box.len * (1 - 2 * ZONE_PAD));
  const maxCap = maxCapMm(box);
  const capped = style.cap > maxCap + 1e-6;
  const fullSize = Math.min(style.cap, maxCap) / box.mmPerPx / capRatio;
  // From the hinge side of the zone, after the margin; capitals centred on the zone's middle line.
  const at = (fontSize: number, reduced: boolean): TextLayout => {
    const width = metrics.measure(shown, fontSize);
    const uCentre = hingeEndU(box) + tipward(box) * (box.len * ZONE_PAD + width / 2);
    const p = zonePoint(box, uCentre, 0);
    return { fontSize, cx: p.x, cy: p.y, angleDeg: box.angleDeg, capMm: fontSize * capRatio * box.mmPerPx, capped, reduced, width, capPx: fontSize * capRatio, free: false };
  };
  const full = at(fullSize, false);
  if (full.width <= avail || full.width <= 0) return full;
  // A201 (David, 11 octobre : « 2,5 mm par défaut, même pour l'intérieur de la branche droite ») : longer than the
  // zone, the text keeps its height and runs on towards the ear while it stays on the temple…
  if (room && room(full)) return full;
  // …otherwise it is reduced uniformly (the glyphs keep their proportions): as much as the temple allows, at least
  // down to the zone's length.
  const fitZone = fullSize * (avail / full.width);
  if (room) {
    let lo = fitZone, hi = fullSize;
    if (room(at(lo, true))) {
      for (let i = 0; i < 10; i++) { const mid = (lo + hi) / 2; if (room(at(mid, true))) lo = mid; else hi = mid; }
    }
    return at(lo, true);
  }
  return at(fitZone, true);
}

/** Where an automatic text is, as a free placement (the start of a drag, a nudge or a tilt). */
export function placeOf(box: ZoneBox, layout: TextLayout): FreePlace {
  return pointPlace(box, layout.cx, layout.cy, layout.angleDeg - box.angleDeg);
}

/** Baseline point to draw the text centred (before the rotation about its centre). */
export function baseline(layout: TextLayout): { x: number; y: number } {
  return { x: layout.cx, y: layout.cy + layout.capPx / 2 };
}

/**
 * Points along the outline of the text's box (capitals and descenders), in image pixels: corners, edge points
 * every ~1/8 of the width — what must stay on the temple.
 */
export function textOutline(layout: TextLayout): Array<{ x: number; y: number }> {
  const rad = (layout.angleDeg * Math.PI) / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  const hw = layout.width / 2;
  const top = -layout.capPx / 2, bottom = layout.capPx / 2 + layout.fontSize * DESCENT_EM;
  const pts: Array<{ x: number; y: number }> = [];
  const n = 8;
  for (let i = 0; i <= n; i++) {
    const u = -hw + (2 * hw * i) / n;
    for (const v of [top, bottom]) pts.push({ x: layout.cx + u * c - v * s, y: layout.cy + u * s + v * c });
  }
  for (const u of [-hw, hw]) {
    const v = (top + bottom) / 2;
    pts.push({ x: layout.cx + u * c - v * s, y: layout.cy + u * s + v * c });
  }
  return pts;
}

/**
 * The wordmark: `LOGO_CAP_MM` high (within the thickness and the zone), on the hinge side. A201: the customer can
 * change its height (the size steps of the texts) and move / tilt it like a text (`style.place`).
 */
export function layoutLogo(box: ZoneBox, style: SlotStyle = { cap: LOGO_CAP_MM, place: null }): LogoLayout {
  if (style.place) {
    const h = style.cap / box.mmPerPx;
    const w = h * LOGO_RATIO;
    const p = placePoint(box, style.place);
    return { x: p.x - w / 2, y: p.y - h / 2, width: w, height: h, angleDeg: box.angleDeg + style.place.rot, cx: p.x, cy: p.y };
  }
  const avail = Math.max(1, box.len * (1 - 2 * ZONE_PAD));
  let height = Math.min(style.cap / box.mmPerPx, box.thick * MAX_CAP_FRACTION);
  let width = height * LOGO_RATIO;
  if (width > avail) { width = avail; height = width / LOGO_RATIO; }
  const inset = box.len * ZONE_PAD;
  const uStart = box.hingeLeft ? -box.len / 2 + inset : box.len / 2 - inset - width;
  // Unrotated box relative to the zone centre; the rotation is applied about (cx, cy).
  return { x: box.cx + uStart, y: box.cy - height / 2, width, height, angleDeg: box.angleDeg, cx: box.cx, cy: box.cy };
}

/** A201: the logo's centre (after its rotation) as a free placement. */
export function logoCentre(logo: LogoLayout): { x: number; y: number } {
  const rad = (logo.angleDeg * Math.PI) / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  const dx = logo.x + logo.width / 2 - logo.cx, dy = logo.y + logo.height / 2 - logo.cy;
  return { x: logo.cx + dx * c - dy * s, y: logo.cy + dx * s + dy * c };
}

export function logoPlace(box: ZoneBox, logo: LogoLayout): FreePlace {
  const centre = logoCentre(logo);
  return pointPlace(box, centre.x, centre.y, logo.angleDeg - box.angleDeg);
}

/** A201: points along the logo's box (what must stay on the temple and off the hinge). */
export function logoOutline(logo: LogoLayout): Array<{ x: number; y: number }> {
  const centre = logoCentre(logo);
  return textOutline({ cx: centre.x, cy: centre.y, angleDeg: logo.angleDeg, width: logo.width, capPx: logo.height, fontSize: 0 } as TextLayout);
}

/** Corners of the zone rectangle (for the dotted / sage / coral outline), as an SVG `points` string. */
export function zoneCorners(box: ZoneBox): string {
  const hl = box.len / 2, ht = box.thick / 2;
  return [[-hl, -ht], [hl, -ht], [hl, ht], [-hl, ht]].map(([u, v]) => { const p = zonePoint(box, u!, v!); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(' ');
}

/** Corners of the text's box (for its outline while it is placed by hand), as an SVG `points` string. */
export function textCorners(layout: TextLayout, pad = 0): string {
  const rad = (layout.angleDeg * Math.PI) / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  const hw = layout.width / 2 + pad;
  const top = -layout.capPx / 2 - pad, bottom = layout.capPx / 2 + layout.fontSize * DESCENT_EM + pad;
  return [[-hw, top], [hw, top], [hw, bottom], [-hw, bottom]].map(([u, v]) => `${(layout.cx + u! * c - v! * s).toFixed(1)},${(layout.cy + u! * s + v! * c).toFixed(1)}`).join(' ');
}

/**
 * Measures with a 2D canvas (letter spacing added per gap) and reads the font's capital height from
 * the « H » glyph. Our symbols are measured in their own font (listed first). Without a canvas (tests), a
 * rough estimate.
 */
export function canvasMeasure(ctx: CanvasRenderingContext2D | null, font: EngraveFont): FontMetrics {
  let capRatio = CAP_RATIO_FALLBACK;
  const family = fontStack(font);
  if (ctx) {
    ctx.font = `${font.weight} 100px ${family}`;
    const m = ctx.measureText('H');
    const asc = (m as TextMetrics & { actualBoundingBoxAscent?: number }).actualBoundingBoxAscent;
    if (typeof asc === 'number' && asc > 20 && asc < 120) capRatio = asc / 100;
  }
  const measure: TextMeasure = (text, fontSize) => {
    const glyphs = Array.from(text.replace(/︎/g, ''));
    const gaps = Math.max(0, glyphs.length - 1);
    if (!ctx) return glyphs.length * fontSize * 0.55 + gaps * font.letterSpacing * fontSize;
    ctx.font = `${font.weight} ${fontSize}px ${family}`;
    return ctx.measureText(text).width + gaps * font.letterSpacing * fontSize;
  };
  return { measure, capRatio };
}

/** Font of a slot's text: one font for every text (A110Z2: the house exception is gone; EB Garamond by default since A201). */
export function slotFont(state: EngraveState, _slot: SlotKey): EngraveFont {
  return fontById(state.font);
}

let measureCanvas: CanvasRenderingContext2D | null | undefined;
/** One 2D context for every measurement (null where there is no canvas, e.g. the tests). */
export function sharedMeasureContext(): CanvasRenderingContext2D | null {
  if (measureCanvas === undefined) {
    try {
      measureCanvas = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    } catch {
      measureCanvas = null;
    }
  }
  return measureCanvas;
}

/**
 * What the size slider can still change for an automatically placed text on this temple: for each step of
 * `CAP_STEPS_MM`, the capital height actually drawn and the length left free (fraction of the zone). Past the
 * temple's thickness or the zone's length, higher steps draw the same height — the controls say so.
 */
export interface TextFit {
  /** Per step: height drawn (mm), share of the zone left free, and which limit applied (temple thickness, zone length). */
  steps: Array<{ capMm: number; free: number; capped: boolean; reduced: boolean }>;
  maxCapMm: number;
}

export function textFit(box: ZoneBox, font: EngraveFont, text: string, metrics: FontMetrics, room?: TextRoom): TextFit | null {
  if (!displayText(text, font)) return null;
  const avail = Math.max(1, box.len * (1 - 2 * ZONE_PAD));
  const steps = CAP_STEPS_MM.map(cap => {
    const l = layoutText(box, font, text, { cap, place: null }, metrics, room)!;
    return { capMm: l.capMm, free: Math.max(0, avail - l.width) / box.len, capped: l.capped, reduced: l.reduced };
  });
  return { steps, maxCapMm: maxCapMm(box) };
}

/** Highest step that still makes the text taller than the step below. */
export function topUsefulStep(fit: TextFit): number {
  let top = 0;
  for (let i = 1; i < fit.steps.length; i++) if (fit.steps[i]!.capMm > fit.steps[top]!.capMm * 1.02) top = i;
  return top;
}

/** The text is drawn smaller than the chosen step (temple too thin, or text too long for the zone). */
export function stepLimited(fit: TextFit, step: number): boolean {
  const s = fit.steps[step];
  return Boolean(s && s.capMm < (CAP_STEPS_MM[step] ?? 0) * 0.98);
}
